<template>
  <section class="spv-page">
    <!-- Empty State (when no SPVs exist) -->
    <div class="spv-page__empty-wrapper" v-if="(!spvRequests || spvRequests?.length === 0) && (!approvedSpvs || approvedSpvs?.length === 0)">
      <SpvEmptyState />
    </div>

    <!-- SPV List View -->
    <div v-else class="spv-page__content">
      <!-- Page Header -->
      <div class="spv-header">
        <div class="spv-header__left">
          <h1 class="spv-header__title">Special Purpose Vehicles</h1>
          <p class="spv-header__description">Your list of SPV documents are shown here.</p>
        </div>
        <div class="spv-header__actions">
          <div class="spv-header__filters">
            <Search
              :showSearchField="showSearchField"
              @search-click="showSearchField = !showSearchField"
              :clearValue="resetSearch"
              @search-input="filterSpv"
            />
            <div class="spv-header__filter-divider" v-if="currentMenu === 'requests'">
              <Filter
                title="SPVs"
                :filterOptions="filterOptions"
                @filter-click="showFilterOptions = !showFilterOptions"
                :showFilterOptions="showFilterOptions"
                @filter-selected="filterSpv"
                @mouseleave="startTimeout"
                @mouseenter="deleteTimeout"
              />
            </div>
          </div>
          <button
            class="spv-header__create-btn"
            @click="showOptions = true"
            :disabled="!(canPerformAction('spv:*') || canPerformAction('spv:addSPV'))"
          >
            Create an SPV
          </button>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="spv-tabs">
        <button
          v-for="(menu, index) in spvMenu"
          :key="index"
          class="spv-tab"
          :class="{ 'spv-tab--active': currentMenu === menu.value }"
          @click="currentMenu = menu.value"
        >
          {{ menu.name }}
        </button>
      </div>

      <!-- Content Area -->
      <div class="spv-list">
        <SmallLoader v-if="loading" />
        <template v-else>
          <SpvCard
            v-for="spv in spvs"
            :key="spv._id"
            :spv="spv"
            @refresh-list="refreshList"
            :isActionAllowed="canPerformAction('spv:*') || canPerformAction('spv:updateSPV')"
          />
          <SpvEmptyState
            v-if="!spvs || spvs?.length === 0"
            :spvType="currentMenu"
            :emptyType="emptyType"
          />
          <SpvOptionsModal
            v-if="showOptions"
            @close-spv-options="showOptions = false"
          />
        </template>

        <!-- Pagination -->
        <div class="spv-pagination" v-if="!filterValue && pages > 1">
          <Pagination
            :currentPage="page"
            :totalPage="pages"
            @pageChange="changePage"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import filter from "@/mixins/filter";
export default {
  mixins: [filter],
};
</script>

<script setup>
import Filter from "@/components/general/Filter";
import Search from "@/components/general/Search";
import Pagination from "@/components/navigation/Pagination.vue";
import SmallLoader from "@/components/notification/SmallLoader.vue";
import SpvCard from "@/components/spv/SpvCard";
import SpvEmptyState from "@/components/spv/SpvEmptyState";
import SpvOptionsModal from "@/components/spv/SpvOptionsModal.vue";
import { useSearch } from "@/composables/search";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { permissionsService } from "@/utils/permissionChecker.js";

const store = useStore();
const { showSearchField } = useSearch();

const spvMenu = [
  { name: "Your SPVs", value: "spvs" },
  { name: "SPV Requests", value: "requests" },
];
const filterOptions = ref(["Pending", "Approved", "Rejected", "Terminated"]);
const currentMenu = ref("spvs");
const showFilterOptions = ref(false);
const filterValue = ref(null);
const filterResult = ref(null);
const showOptions = ref(false);
const page = ref(1);
const pages = ref(1);
const limit = ref(7);
const resetSearch = ref(false);

const permissions = ref(null);
permissions.value = permissionsService.loadUserPermissions();

const canPerformAction = (action) => {
  return permissionsService.hasPermission(action);
};

const loading = computed(() => store.getters["loading"]);
const spvRequests = computed(() => store.getters["Spv/spvRequests"]);
const spvRequestsCount = computed(() => store.getters["Spv/spvRequestsCount"]);
const currentSpvRequestsPage = computed(() => store.getters["Spv/currentSpvRequestsPage"]);
const totalSpvRequestsPages = computed(() => store.getters["Spv/totalSpvRequestsPages"]);
const approvedSpvs = computed(() => store.getters["Spv/spvs"]);
const spvCount = computed(() => store.getters["Spv/spvCount"]);
const CurrentSpvPage = computed(() => store.getters["Spv/CurrentSpvPage"]);
const totalSpvPages = computed(() => store.getters["Spv/totalSpvPages"]);

const spvs = computed(() => {
  let spvs = null;
  if (filterValue.value?.value) {
    spvs = filterResult.value;
  } else {
    switch (currentMenu.value) {
      case "spvs":
        spvs = approvedSpvs.value;
        break;
      case "requests":
        spvs = spvRequests.value;
        break;
    }
  }
  return spvs;
});

const emptyType = computed(() => {
  let type = null;
  if (filterValue.value) {
    if (filterValue.value.type) {
      type = "search";
    } else {
      type = "filter";
    }
  } else {
    type = "default";
  }
  return type;
});

const filterSpvDocByName = () => {
  const name = filterValue.value.value;
  const filteredDocs = approvedSpvs.value.filter((spv) =>
    spv.name.toLowerCase().includes(name)
  );
  return filteredDocs;
};

const filterSpvRequestByName = async () => {
  const { value: name } = filterValue.value;
  const response = await store.dispatch("Spv/searchSpvRequest", name);
  const { data: spv } = response.data;
  filterResult.value = spv;
};

const filterSpvRequestByStatus = async ({ value }) => {
  const params = {
    page: page.value,
    limit: limit.value,
    status: value,
  };
  await store.dispatch("Spv/fetchSpvRequests", params);
  filterResult.value = spvRequests.value;
};

const fetchSpvRequests = async () => {
  const params = {
    page: page.value,
    limit: limit.value,
    status: "Pending",
  };
  await store.dispatch("Spv/fetchSpvRequests", params);
};

const fetchSpvs = async () => {
  const params = {
    page: page.value,
    limit: limit.value,
    status: "completed",
  };
  await store.dispatch("Spv/fetchSpvs", params);
  pages.value = totalSpvPages.value;
};

const refreshList = () => {
  const menu = currentMenu.value;
  switch (menu) {
    case "requests":
      fetchSpvRequests();
      break;
    case "spvs":
      fetchSpvs();
      break;
  }
};

const filterSpv = (object) => {
  filterValue.value = object;
};

const changePage = (value) => {
  page.value = value;
};

watch(currentMenu, (value) => {
  filterValue.value = null;
  page.value = 1;
  switch (value) {
    case "requests":
      pages.value = totalSpvRequestsPages.value;
      break;
    case "spvs":
      pages.value = totalSpvPages.value;
      break;
  }
});

watch(filterValue, (filter) => {
  if (filter?.value) {
    const { type } = filter;
    if (type === "name") {
      filterSpvRequestByName();
    } else {
      filterSpvRequestByStatus(filter);
    }
  } else {
    filterValue.value = null;
    resetSearch.value = true;
    fetchSpvRequests();
    setTimeout(() => {
      resetSearch.value = false;
    }, 500);
  }
});

watch(page, () => {
  const menu = currentMenu.value;
  switch (menu) {
    case "requests":
      fetchSpvRequests();
      break;
    case "spvs":
      fetchSpvs();
      break;
  }
});

fetchSpvRequests();
fetchSpvs();
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-page: #F8FAFC;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.spv-page {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 32px 16px 0;
  box-sizing: border-box;

  @media (min-width: 1536px) {
    max-width: 1200px;
  }

  &__empty-wrapper {
    padding: 40px 0;
  }

  &__content {
    padding-bottom: 40px;
  }
}

.spv-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  &__left {
    flex: 1;
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 8px;
  }

  &__description {
    font-size: 14px;
    color: $text-muted;
    margin: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 640px) {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__filter-divider {
    border-left: 1px solid $border-color;
    padding-left: 8px;
    height: 24px;
    display: flex;
    align-items: center;
  }

  &__create-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    background: $btn-primary;
    color: white;
    border: none;
    border-radius: 7px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
    white-space: nowrap;

    &:hover {
      background: $btn-primary-hover;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.spv-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid $border-color;
  padding-bottom: 0;
}

.spv-tab {
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: -1px;

  &:hover {
    color: $text-secondary;
  }

  &--active {
    color: $text-primary;
    border-bottom-color: $btn-primary;
  }
}

.spv-list {
  min-height: 200px;
}

.spv-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}
</style>
